import type { Experiment } from 'types';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { useCombinationReport } from '@/state/experiments/useCombinationReport';
import { Layers } from 'lucide-react';
import { FirstRunLink, FirstRunState } from '../../FirstRunState';
import { ExperimentSelector } from './ExperimentSelector';
import { InteractionTable } from './InteractionTable';
import { NoWindowNotice } from './NoWindowNotice';
import { Selectors } from './Selectors';

/**
 * Cross-experiment combination analysis, based on conversions alone.
 *
 * The report asks whether two changes interact and whether order values differ. It cannot ask
 * which combination converts best: that needs exposure per combination, which is not
 * recorded in a usable form. See combination/interaction.ts on the server for why, and why
 * the interaction is measurable regardless.
 */
export const CombinationReport = () => {
  const apiClient = useClient();
  const { website } = useWebsites();
  const websiteId = website?.id || '';

  const { data: allExperiments } = useQuery({
    queryKey: ['EXPERIMENTS', websiteId],
    enabled: !!websiteId,
    queryFn: () => apiClient.get<Experiment[]>(`/websites/${websiteId}/experiments`),
  });

  const manager = useCombinationReport(allExperiments);

  // Interaction needs two experiments to compare, so with fewer there is nothing to select.
  if (allExperiments && allExperiments.length < 2) {
    return (
      <FirstRunState
        icon={Layers}
        title="Needs at least two experiments"
        description="This report asks whether two changes running together helped or hindered each other, measured from the conversions that carry both. Run two experiments on the same website and it appears here."
        action={<FirstRunLink to="/experiments/library">Go to experiments</FirstRunLink>}
      />
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="space-y-1">
        <h1 className="text-title font-semibold tracking-tight">Variant Combination Report</h1>
        <p className="supporting max-w-3xl">
          Whether two experiments running together helped or hindered each other, measured from the conversions that
          carry both.
        </p>
      </div>

      <ExperimentSelector
        availableExperiments={manager.availableExperiments}
        experimentIds={manager.experimentIds}
        addExperiment={manager.addExperiment}
        removeExperiment={manager.removeExperiment}
        tourAnchor="combination-experiments"
      />

      {manager.isReady && <Selectors goals={manager.GOALS} goal={manager.goal} setGoal={manager.setGoal} />}

      {manager.isLoading && manager.isReady && <p className="supporting py-12 text-center">Loading conversions...</p>}

      {manager.data && (
        <>
          {manager.reason === 'no-shared-randomising-window' ? (
            <NoWindowNotice experiments={manager.data.experiments} />
          ) : manager.data.combinations.length === 0 ? (
            <div className="border-line rounded-panel border border-dashed py-10 text-center">
              <p className="text-body font-medium">No conversions carry all of the selected experiments.</p>
              <p className="supporting mx-auto max-w-xl pt-1">
                They randomised together, but no visitor who was in all of them went on to convert during that window.
              </p>
            </div>
          ) : (
            <InteractionTable
              combinations={manager.data.combinations}
              interactions={manager.data.interactions}
              experiments={manager.data.experiments}
              interactionAvailable={manager.interactionAvailable}
              window={manager.window}
            />
          )}
        </>
      )}

      {manager.error && (
        <div className="bg-danger-bg border-danger-line rounded-panel space-y-1 border py-12 text-center">
          <p className="text-danger text-body font-medium">Could not build the combination report.</p>
          <p className="supporting">{errorMessage(manager.error)}</p>
        </div>
      )}
    </div>
  );
};

/** useClient throws { status, message } carrying the server's text. */
const errorMessage = (error: unknown): string => {
  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' && message.trim() ? message : 'Please try again.';
};
